import { useRef, useState, useMemo } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useDispatch } from "react-redux";
import { SearchBox } from "../common/SearchBox";
import FormModel from "../common/FormModel";
import GroupedStockSubTable from "./GroupedStockSubTable";
import exportCSV from "../../../utils/exportCSV";
import { ActionButton } from "../common/Buttons";
import api from "../../../utils/axiosInstance";
import { showAlert } from "../../../redux/alertSlice";

const tableConfig = [
  {
    key: "tagNo",
    label: "Tag No.",
    width: "6rem",
    align: "left",
    dataKey: "tagNo",
  },
  {
    key: "pcs",
    label: "Pcs",
    width: "4rem",
    align: "right",
    dataKey: "pcs",
  },
  {
    key: "grossWt",
    label: "Gross Wt.",
    width: "6rem",
    align: "right",
    dataKey: "grossWt",
  },
  {
    key: "netWt",
    label: "Net Wt",
    width: "6rem",
    align: "right",
    dataKey: "netWt",
  },
  {
    key: "model",
    label: "Model",
    width: "16rem",
    align: "left",
    dataKey: "model",
  },
  {
    key: "detail",
    label: "Detail",
    width: "20rem",
    align: "left",
    dataKey: "detail",
  },
  {
    key: "tagDate",
    label: "Tag Date.",
    width: "7rem",
    align: "right",
    dataKey: "tagDate",
  },
];
const tableConfig2 = [
  {
    key: "tagNo",
    label: "Tag No.",
    width: "6rem",
    align: "left",
    dataKey: "tagNo",
  },
  {
    key: "pcs",
    label: "Pcs",
    width: "4rem",
    align: "right",
    dataKey: "pcs",
  },
  {
    key: "grossWt",
    label: "Gross Wt.",
    width: "6rem",
    align: "right",
    dataKey: "grossWt",
  },
  {
    key: "value",
    label: "Value",
    width: "7rem",
    align: "right",
    dataKey: "value",
  },
  {
    key: "model",
    label: "Model",
    width: "16rem",
    align: "left",
    dataKey: "model",
  },
  {
    key: "detail",
    label: "Detail",
    width: "20rem",
    align: "left",
    dataKey: "detail",
  },
  {
    key: "tagDate",
    label: "Tag Date.",
    width: "7rem",
    align: "right",
    dataKey: "tagDate",
  },
];

const GroupedStockReportTable = ({
  tableData,
  loading,
  totals,
  columns = [],
  searchFields = ["isbnno", "prodname"],
  reportType, // "G" | "D" — which group the summary rows belong to
  webid,
  asOnDate,
}) => {
  const [search, setSearch] = useState("");
  const scrollRef = useRef(null);
  const dispatch = useDispatch();

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null, // 'asc' | 'desc' | null
  });

  const [modelOpen, setModelOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [detailData, setDetailData] = useState({ tableData: [], totals: {} });
  const [detailLoading, setDetailLoading] = useState(false);

  const detailColumns = reportType === "D" ? tableConfig2 : tableConfig;

  const openDetail = async (row) => {
    if (row.isSubtotal || !row.mcode || !row.headcode) return;
    setSelectedRow(row);
    setModelOpen(true);
    setDetailLoading(true);
    try {
      const response = await api.post("/branch-reports/get-grouped-stock-detail", {
        asOnDate,
        type: reportType,
        webid,
        mcode: row.mcode,
        headcode: row.headcode,
      });
      if (response.data?.success) {
        setDetailData({ tableData: response.data.data, totals: response.data.totals });
      } else {
        setDetailData({ tableData: [], totals: {} });
        dispatch(
          showAlert({
            message: response.data.message || "No detail records found",
            severity: "warning",
          })
        );
      }
    } catch (error) {
      console.log(error);
      setDetailData({ tableData: [], totals: {} });
      dispatch(showAlert({ message: "Error fetching detail", severity: "error" }));
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSort = (columnKey) => {
    setSortConfig((prev) => {
      if (prev.key === columnKey) {
        // Cycle: asc → desc → none
        if (prev.direction === "asc")
          return { key: columnKey, direction: "desc" };
        if (prev.direction === "desc") return { key: null, direction: null };
        return { key: columnKey, direction: "asc" };
      }
      return { key: columnKey, direction: "asc" }; // first click
    });
  };

  // Filter data
  const filteredData = useMemo(() => {
    if (!search.trim()) return tableData;
    const searchLower = search.trim().toLowerCase();
    return tableData.filter((item) =>
      searchFields.some((field) =>
        item[field]?.toString().toLowerCase().includes(searchLower)
      )
    );
  }, [tableData, search, searchFields]);

  // Virtualizer
  const rowVirtualizer = useVirtualizer({
    count: filteredData?.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 42,
    overscan: 8,
  });

  const sortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return filteredData;

    const sorted = [...filteredData].sort((a, b) => {
      const x = a[sortConfig.key];
      const y = b[sortConfig.key];

      if (x == null) return 1;
      if (y == null) return -1;

      if (typeof x === "number" && typeof y === "number") {
        return sortConfig.direction === "asc" ? x - y : y - x;
      }

      return sortConfig.direction === "asc"
        ? x.toString().localeCompare(y.toString())
        : y.toString().localeCompare(x.toString());
    });

    return sorted;
  }, [filteredData, sortConfig]);

  const TableCellHeader = ({
    column,
    value,
    className = "",
    isHeader = false,
  }) => {
    const isActive = sortConfig.key === column.key;
    return (
      <p
        className={`text-${column.align} overflow-hidden px-1 cursor-pointer ${className}`}
        style={{ flexBasis: column.width, flexShrink: 0 }}
        title={value?.toString()}
        onClick={() => handleSort(column.key)}
        onMouseDown={(e) => e.preventDefault()}
      >
        {!isHeader && column.format ? column.format(value) : value}
        {/* Icons */}
        {isActive &&
          (sortConfig.direction === "asc"
            ? "↑"
            : sortConfig.direction === "desc"
            ? "↓"
            : "")}
      </p>
    );
  };
  const TableCell = ({
    column,
    value,
    className = "",
    isHeader = false,
    index,
  }) => {
    return (
      <>
        <p
          className={`cursor-pointer text-${column.align} overflow-hidden px-1 ${className}`}
          style={{ flexBasis: column.width, flexShrink: 0 }}
          title={value?.toString()}
        >
          {!isHeader && column.format ? column.format(value) : value}
        </p>
      </>
    );
  };

  // csv export function
  const handleCsvExport = () => {
    if (!sortedData?.length) return;
    exportCSV(sortedData, columns, "grouped-stock-report-data");
  };

  return (
    <div className="w-full h-full text-sm flex flex-col overflow-hidden border rounded-[15px] shadow-custom-light">
      {modelOpen && (
        <FormModel onClose={() => setModelOpen(false)}>
          <GroupedStockSubTable
            tableData={detailData.tableData}
            totals={detailData.totals}
            loading={detailLoading}
            columns={detailColumns}
            searchFields={["tagNo", "model"]}
            selectedRow={selectedRow}
          />
        </FormModel>
      )}
      {/* Search Header */}
      <div className="w-full bg-sixth text-[#ffffff]">
        <div className="flex justify-between items-center px-4 pt-3">
          <div className="flex items-center gap-2 px-2 py-1 md:w-80">
            <SearchBox
              placeholder="Search orders..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              disabled={loading || tableData.length === 0}
            />
          </div>
          {sortedData?.length > 0 && (
            <ActionButton
              text={"Export CSV"}
              onClick={handleCsvExport}
              customStyle=""
            />
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-x-auto">
        <div className="min-w-fit h-full flex flex-col">
          {/* Table Header */}
          <div className="flex font-semibold px-3 py-2 pr-4 border-b bg-sixth text-[#ffffff] flex-shrink-0">
            {columns.map((col) => {
              return (
                <TableCellHeader
                  key={col.key}
                  column={col}
                  value={col.label}
                  isHeader={true}
                />
              );
            })}
          </div>

          {/* Virtualized Rows */}
          <div
            ref={scrollRef}
            className="h-[300px] overflow-y-auto font-mont font-bold text-sm"
          >
            <div
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                position: "relative",
              }}
            >
              {loading ? (
                <div className="w-full h-36 flex justify-center items-center">
                  {/* <LoaderCircle className="animate-spin" /> */}
                  Loading...
                </div>
              ) : sortedData.length === 0 ? (
                <div className="">
                  <img
                    className="w-64 mx-auto"
                    src="/table/tablebg.jpg"
                    alt=""
                  />
                  <div className="text-center font-mont font-semibold text-gray-400">
                    No results yet.
                  </div>
                </div>
              ) : (
                rowVirtualizer.getVirtualItems().map((virtualItem) => {
                  // const item = filteredData[virtualItem.index];
                  const item = sortedData[virtualItem.index];
                  return (
                    <div
                      key={virtualItem.key}
                      onClick={() => openDetail(item)}
                      className="flex font-semibold px-3 py-2 text-sm border-b border-b-[var(--table-row-border)] absolute top-0 left-0 w-full"
                      style={{
                        height: `${virtualItem.size}px`,
                        transform: `translateY(${virtualItem.start}px)`,
                      }}
                    >
                      {columns.map((col, i) => (
                        <TableCell
                          key={col.key}
                          column={col}
                          value={item[col.dataKey]}
                          index={i}
                        />
                      ))}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Table Footer */}
          <div className="flex font-semibold px-3 py-2 pr-3 border-t bg-sixth text-[#ffffff] flex-shrink-0">
            {columns.map((col, idx) => (
              <TableCell
                key={col.key}
                column={col}
                value={
                  idx === 0
                    ? `Total - (${filteredData?.length} items)`
                    : totals?.[col.dataKey] ?? ""
                }
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupedStockReportTable;
